import { Box, Paper, Stack, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined'
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import Sparkline from './Sparkline'
import type { KpiDatum } from './dashboardTypes'

const ICONS = {
  video: VideocamOutlinedIcon,
  people: PeopleOutlineIcon,
  check: CheckCircleOutlineIcon,
} as const

type KpiCardProps = {
  label: string
  value: string
  suffix?: string
  delta: string
  deltaKind: KpiDatum['deltaKind']
  spark: number[]
  icon: KpiDatum['icon']
}

export default function KpiCard({ label, value, suffix, delta, deltaKind, spark, icon }: KpiCardProps) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const Icon = ICONS[icon]
  const deltaColor = deltaKind === 'pos' ? c.success : deltaKind === 'neg' ? c.danger : c.ink56

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.25,
        borderRadius: `${theme.affexy.radii.md}px`,
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
        minWidth: 0,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="overline" sx={{ fontSize: 11, color: c.ink48, lineHeight: 1.4 }}>
          {label}
        </Typography>
        <Box
          sx={{
            width: 26,
            height: 26,
            borderRadius: `${theme.affexy.radii.sm}px`,
            bgcolor: c.gray50,
            display: 'grid',
            placeItems: 'center',
            color: c.ink56,
          }}
        >
          <Icon sx={{ fontSize: 15 }} />
        </Box>
      </Box>

      <Stack direction="row" alignItems="baseline" spacing={0.5}>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: 30,
            lineHeight: 1,
            letterSpacing: '-0.01em',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {value}
        </Typography>
        {suffix && <Typography sx={{ fontSize: 14, color: c.ink56 }}>{suffix}</Typography>}
      </Stack>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            fontSize: 12,
            fontWeight: 600,
            color: deltaColor,
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap',
          }}
        >
          {deltaKind === 'pos' ? (
            <ArrowDropUpIcon sx={{ fontSize: 18, mr: -0.25 }} />
          ) : deltaKind === 'neg' ? (
            <ArrowDropDownIcon sx={{ fontSize: 18, mr: -0.25 }} />
          ) : null}
          {delta}
        </Box>
        <Sparkline points={spark} color={deltaKind === 'neg' ? c.danger : c.coral} />
      </Box>
    </Paper>
  )
}
